import { useEffect } from 'react';

/**
 * Forces every copy made from the page to land on the clipboard as PLAIN TEXT
 * only — no styling, colors, or background travel into the paste target.
 *
 * Why this is needed: when you highlight rendered text and press Cmd/Ctrl+C (or
 * right-click > Copy, or use the Edit menu), the browser writes TWO clipboard
 * flavors — `text/plain` AND `text/html`. The `text/html` flavor carries the
 * page's computed styles (the surface background, text color, font), so pasting
 * into a rich-text field (EMR, Word, email) reproduces those backgrounds.
 *
 * We intercept the `copy` event and rewrite the clipboard with the selection's
 * text only, deliberately NOT setting `text/html`. Newlines are preserved
 * verbatim: the clinical note bodies render with `white-space: pre-wrap` / in
 * `<pre>`, so `Selection.toString()` returns their literal line breaks.
 *
 * The per-section Copy buttons already copy clean text via `writeText`; this
 * closes the remaining gap for manual text selection.
 */
export function usePlainTextCopy(): void {
  useEffect(() => {
    const onCopy = (e: ClipboardEvent) => {
      // Leave native inputs/textareas alone — their selection lives outside the
      // document selection and is already plain text; don't interfere with editing.
      const tag = (document.activeElement as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea') return;

      const text = window.getSelection()?.toString() ?? '';
      if (!text) return; // nothing selected on the page — let the browser handle it

      const data = e.clipboardData;
      if (!data) return; // ancient browsers without clipboardData — keep native behavior

      e.preventDefault();
      data.setData('text/plain', text);
      // Intentionally no data.setData('text/html', …) → styles/background cannot travel.
    };

    document.addEventListener('copy', onCopy);
    return () => document.removeEventListener('copy', onCopy);
  }, []);
}

export default usePlainTextCopy;
